/**
 * PPD (Personal and Professional Development) Service
 * 
 * Handles all PPD related API calls to the DAB backend
 * Follows the same pattern as businessCase.ts and travel.ts
 */

import { getDabUrl } from './api';

// ============================================================================
// INTERFACES - Following businessCase.ts pattern
// ============================================================================

export interface Competency {
    id: number;
    competency_name: string;
    display_order: number;
}

export interface PPDFormServer {
    id?: number;
    owner_email: string;
    owner_name: string;
    manager_email: string;
    submission_date?: string;
    status: string;

    // Header fields
    job_title?: string;
    activity?: string;
    provider_if_external?: string;
    duration?: string;
    cost_gbp?: number;
    financial_year_start?: number;

    // 9 Questions
    development_need?: string;
    options_considered?: string;
    resources_required?: string;
    timescales?: string;
    effectiveness_assessment?: string;
    expected_changes?: string;
    knowledge_sharing?: string;
    proposed_option?: string;

    // Approval fields
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;
    admin_comments?: string;

    created_at?: string;
    updated_at?: string;
}

export interface PPDFormClient {
    id?: number;
    owner_email: string;
    owner_name: string;
    manager_email: string;
    submission_date?: string;
    status: 'pending' | 'approved' | 'rejected' | 'needs-discussion';

    // Header fields
    job_title: string;
    activity: string;
    provider_if_external: string;
    duration: string;
    cost_gbp: number;
    financial_year_start?: number;

    // 9 Questions
    development_need: string;
    selected_competencies: number[]; // Array of competency IDs
    options_considered: string;
    resources_required: string;
    timescales: string;
    effectiveness_assessment: string;
    expected_changes: string;
    knowledge_sharing: string;
    proposed_option: string;

    // Approval fields (read-only for users)
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;
    admin_comments?: string;

    created_at?: string;
    updated_at?: string;
}

export interface PPDFormWithCompetencies extends PPDFormServer {
    competencies?: string; // Comma-separated competency names from view
}

export interface PPDFormCompetency {
    id?: number;
    ppd_form_id: number;
    competency_id: number;
}

// ============================================================================
// CONVERSION FUNCTIONS - Following businessCase.ts pattern
// ============================================================================

/**
 * Convert server format to client format
 */
export function fromServerPPD(server: PPDFormWithCompetencies, competencyIds: number[] = []): PPDFormClient {
    return {
        id: server.id,
        owner_email: server.owner_email,
        owner_name: server.owner_name,
        manager_email: server.manager_email,
        submission_date: server.submission_date,
        status: server.status as 'pending' | 'approved' | 'rejected' | 'needs-discussion',
        job_title: server.job_title || '',
        activity: server.activity || '',
        provider_if_external: server.provider_if_external || '',
        duration: server.duration || '',
        cost_gbp: server.cost_gbp || 0,
        financial_year_start: server.financial_year_start,
        development_need: server.development_need || '',
        selected_competencies: competencyIds,
        options_considered: server.options_considered || '',
        resources_required: server.resources_required || '',
        timescales: server.timescales || '',
        effectiveness_assessment: server.effectiveness_assessment || '',
        expected_changes: server.expected_changes || '',
        knowledge_sharing: server.knowledge_sharing || '',
        proposed_option: server.proposed_option || '',
        approved_by: server.approved_by,
        approval_date: server.approval_date,
        rejection_reason: server.rejection_reason,
        admin_comments: server.admin_comments,
        created_at: server.created_at,
        updated_at: server.updated_at
    };
}

/**
 * Convert client format to server format for submission
 */
export function toServerPPD(client: PPDFormClient): Omit<PPDFormServer, 'id' | 'created_at' | 'updated_at' | 'submission_date'> {
    return {
        owner_email: client.owner_email,
        owner_name: client.owner_name,
        manager_email: client.manager_email,
        status: client.status,
        job_title: client.job_title || undefined,
        activity: client.activity || undefined,
        provider_if_external: client.provider_if_external || undefined,
        duration: client.duration || undefined,
        cost_gbp: client.cost_gbp || 0,
        development_need: client.development_need || undefined,
        options_considered: client.options_considered || undefined,
        resources_required: client.resources_required || undefined,
        timescales: client.timescales || undefined,
        effectiveness_assessment: client.effectiveness_assessment || undefined,
        expected_changes: client.expected_changes || undefined,
        knowledge_sharing: client.knowledge_sharing || undefined,
        proposed_option: client.proposed_option || undefined,
        approved_by: undefined,
        approval_date: undefined,
        rejection_reason: undefined,
        admin_comments: undefined
    };
}

// ============================================================================
// API FUNCTIONS - Following businessCase.ts and travel.ts patterns
// ============================================================================

/**
 * Fetch all competency options (for Question 2 dropdown)
 */
export async function fetchCompetencies(): Promise<Competency[]> {
    console.log('Fetching competencies from DAB...');

    const response = await fetch(getDabUrl('PPDCompetencies?$orderby=display_order asc'), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch competencies:', response.status, errorText);
        throw new Error(`Failed to fetch competencies: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Competencies fetched:', data.value?.length || 0);
    return data.value || [];
}

/**
 * Submit a new PPD form with selected competencies
 * Multi-step: 1) Create form, 2) Link competencies
 */
export async function submitPPD(ppdForm: PPDFormClient): Promise<PPDFormClient> {
    console.log('Submitting PPD form:', ppdForm);

    // Step 1: Create the main PPD form
    const response = await fetch(getDabUrl('PPDForms'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(toServerPPD(ppdForm))
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to submit PPD form:', response.status, errorText);
        throw new Error(`Failed to submit PPD form: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    const createdForm = data.value[0];
    console.log('PPD form created successfully:', createdForm);

    // Step 2: Link selected competencies to the form
    if (ppdForm.selected_competencies && ppdForm.selected_competencies.length > 0) {
        console.log(`Linking ${ppdForm.selected_competencies.length} competencies...`);

        for (const competencyId of ppdForm.selected_competencies) {
            const linkResponse = await fetch(getDabUrl('PPDFormCompetencies'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    ppd_form_id: createdForm.id,
                    competency_id: competencyId
                })
            });

            if (!linkResponse.ok) {
                console.error(`Failed to link competency ${competencyId}`);
                // Continue with other competencies even if one fails
            } else {
                console.log(`Competency ${competencyId} linked successfully`);
            }
        }
    }

    return fromServerPPD(createdForm, ppdForm.selected_competencies);
}

/**
 * Fetch PPD forms for a specific user
 */
export async function fetchMyPPDs(ownerEmail: string): Promise<PPDFormClient[]> {
    console.log('Fetching PPDs for user:', ownerEmail);

    const response = await fetch(
        getDabUrl(`PPDWithCompetencies?$filter=owner_email eq '${ownerEmail}'&$orderby=submission_date desc`),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        }
    );

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch user PPDs:', response.status, errorText);
        throw new Error(`Failed to fetch PPDs: ${response.statusText}`);
    }

    const data = await response.json();
    const forms = data.value || [];
    console.log(`Fetched ${forms.length} PPD forms for user`);

    // For each form, fetch its competencies
    const formsWithCompetencies = await Promise.all(
        forms.map(async (form: PPDFormWithCompetencies) => {
            const compIds = await fetchFormCompetencies(form.id!);
            return fromServerPPD(form, compIds);
        })
    );

    return formsWithCompetencies;
}

/**
 * Fetch competency IDs for a specific form
 */
export async function fetchFormCompetencies(formId: number): Promise<number[]> {
    const response = await fetch(
        getDabUrl(`PPDFormCompetencies?$filter=ppd_form_id eq ${formId}`),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        }
    );

    if (!response.ok) {
        console.error(`Failed to fetch competencies for form ${formId}`);
        return [];
    }

    const data = await response.json();
    const competencies = data.value || [];
    return competencies.map((c: PPDFormCompetency) => c.competency_id);
}

/**
 * Fetch all PPD forms (Admin only)
 * Uses PPDWithCompetencies view to get all form fields
 */
export async function fetchAllPPDs(): Promise<PPDFormWithCompetencies[]> {
    console.log('Fetching all PPDs for admin...');

    const response = await fetch(
        getDabUrl(`PPDWithCompetencies?$orderby=submission_date desc`),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        }
    );

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch PPDs:', response.status, errorText);
        throw new Error(`Failed to fetch PPDs: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`Fetched ${data.value?.length || 0} PPD forms`);
    return data.value || [];
}

/**
 * Fetch a specific PPD form by ID (with competencies)
 */
export async function fetchPPDById(id: number): Promise<PPDFormClient> {
    console.log('Fetching PPD form:', id);

    const response = await fetch(
        getDabUrl(`PPDWithCompetencies/id/${id}`),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        }
    );

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch PPD form:', response.status, errorText);
        throw new Error(`Failed to fetch PPD form: ${response.statusText}`);
    }

    const data = await response.json();
    const form = data.value[0];
    const compIds = await fetchFormCompetencies(id);

    return fromServerPPD(form, compIds);
}

/**
 * Update the cost of a PPD form (Admin only)
 */
export async function updatePPDCost(id: number, cost: number): Promise<void> {
    console.log('Updating PPD cost:', id, 'to', cost);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            cost_gbp: cost
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update PPD cost:', response.status, errorText);
        throw new Error(`Failed to update PPD cost: ${response.statusText}`);
    }

    console.log('PPD cost updated successfully');
}

/**
 * Assign a PPD cost to a financial year (Admin only).
 * The stored value is the calendar year in which the May-April financial year starts.
 */
export async function updatePPDFinancialYear(id: number, financialYearStart: number): Promise<void> {
    console.log('Updating PPD financial year:', id, 'to', financialYearStart);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            financial_year_start: financialYearStart
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update PPD financial year:', response.status, errorText);
        throw new Error(`Failed to update PPD financial year: ${response.statusText}`);
    }

    console.log('PPD financial year updated successfully');
}

/**
 * Approve a PPD form (Admin only)
 */
export async function approvePPD(id: number, approvedBy: string): Promise<void> {
    console.log('Approving PPD form:', id, 'by', approvedBy);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            status: 'approved',
            approved_by: approvedBy,
            approval_date: new Date().toISOString(),
            rejection_reason: null,
            admin_comments: null
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to approve PPD:', response.status, errorText);
        throw new Error(`Failed to approve PPD: ${response.statusText}`);
    }

    console.log('PPD approved successfully');
}

/**
 * Reject a PPD form (Admin only)
 */
export async function rejectPPD(id: number, approvedBy: string, reason: string): Promise<void> {
    console.log('Rejecting PPD form:', id, 'by', approvedBy);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            status: 'rejected',
            approved_by: approvedBy,
            approval_date: new Date().toISOString(),
            rejection_reason: reason,
            admin_comments: reason
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to reject PPD:', response.status, errorText);
        throw new Error(`Failed to reject PPD: ${response.statusText}`);
    }

    console.log('PPD rejected successfully');
}

/**
 * Mark a PPD form as needs discussion (Admin only)
 */
export async function markPPDNeedsDiscussion(id: number, approvedBy: string, comments: string): Promise<void> {
    console.log('Marking PPD form as needs discussion:', id, 'by', approvedBy);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            status: 'needs-discussion',
            approved_by: approvedBy,
            approval_date: new Date().toISOString(),
            rejection_reason: null,
            admin_comments: comments
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to mark PPD as needs discussion:', response.status, errorText);
        throw new Error(`Failed to mark PPD as needs discussion: ${response.statusText}`);
    }

    console.log('PPD marked as needs discussion successfully');
}

/**
 * Update an existing PPD form
 */
export async function updatePPD(id: number, ppdForm: Partial<PPDFormClient>): Promise<PPDFormClient> {
    console.log('Updating PPD form:', id);

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(toServerPPD(ppdForm as PPDFormClient))
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update PPD:', response.status, errorText);
        throw new Error(`Failed to update PPD: ${response.statusText}`);
    }

    const data = await response.json();
    const updatedForm = data.value[0];
    const compIds = ppdForm.selected_competencies || [];

    return fromServerPPD(updatedForm, compIds);
}

/**
 * Resubmit an existing PPD form (single-record workflow)
 * Updates same record, resets status to pending, and syncs competencies.
 */
export async function resubmitPPD(id: number, ppdForm: PPDFormClient): Promise<PPDFormClient> {
    console.log('Resubmitting PPD form:', id);

    const payload = {
        ...toServerPPD({
            ...ppdForm,
            status: 'pending'
        }),
        status: 'pending',
        approved_by: null,
        approval_date: null,
        rejection_reason: null,
        admin_comments: null
    };

    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to resubmit PPD:', response.status, errorText);
        throw new Error(`Failed to resubmit PPD: ${response.statusText}`);
    }

    const existingCompIds = await fetchFormCompetencies(id);
    const targetCompIds = ppdForm.selected_competencies || [];

    const toDelete = existingCompIds.filter(compId => !targetCompIds.includes(compId));
    const toAdd = targetCompIds.filter(compId => !existingCompIds.includes(compId));

    for (const compId of toDelete) {
        await fetch(getDabUrl(`PPDFormCompetencies/ppd_form_id/${id}/competency_id/${compId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
        });
    }

    for (const compId of toAdd) {
        await fetch(getDabUrl('PPDFormCompetencies'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                ppd_form_id: id,
                competency_id: compId
            })
        });
    }

    const updated = await fetchPPDById(id);
    return updated;
}

/**
 * Delete a PPD form and its associated competencies
 */
export async function deletePPD(id: number): Promise<void> {
    console.log('Deleting PPD form:', id);

    // First, delete associated competency links
    const compIds = await fetchFormCompetencies(id);
    for (const compId of compIds) {
        await fetch(getDabUrl(`PPDFormCompetencies/ppd_form_id/${id}/competency_id/${compId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
        });
    }

    // Then delete the main form
    const response = await fetch(getDabUrl(`PPDForms/id/${id}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to delete PPD:', response.status, errorText);
        throw new Error(`Failed to delete PPD: ${response.statusText}`);
    }

    console.log('PPD deleted successfully');
}

/**
 * Fetch line manager email for a staff member from cappture-v2 database
 * @param staffEmail - The email of the staff member
 * @returns Line manager email or null if not found
 */
export async function fetchLineManagerEmail(staffEmail: string): Promise<string | null> {
    console.log('Fetching line manager for:', staffEmail);

    try {
        const response = await fetch(`/api/GetLineManagerFunction?email=${encodeURIComponent(staffEmail)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        });

        if (!response.ok) {
            if (response.status === 404) {
                console.log('No staff record found for:', staffEmail);
                return null;
            }
            console.error('Failed to fetch line manager:', response.status);
            return null;
        }

        const data = await response.json();
        console.log('Line manager lookup result:', data);

        if (data.found && data.lineManagerEmail) {
            return data.lineManagerEmail;
        }

        return null;
    } catch (error) {
        console.error('Error fetching line manager:', error);
        return null;
    }
}
